import { useEffect, useId, useState } from "react";
import { MOTIONS, type ClipAnimation, type Motion } from "./animation.ts";
import { Group, Slider } from "./controls.tsx";
import type { Capabilities } from "./capabilities.ts";
import {
  COMPOSITION_PRESETS,
  MAX_SIDE,
  MIN_SIDE,
  footageSize,
  matchingComposition,
  setAnimation,
  setComposition,
  unsizeComposition,
  type Clip,
  type CompositionPreset,
  type Project,
} from "./timeline.ts";

/**
 * The frame: how big, and what shape.
 *
 * The presets are the point. Nobody wants to remember that a Reel is 1080 by
 * 1920; they want the button that says Vertical. The two fields under them
 * are for the sizes the presets do not cover, and the footage button is the
 * way back to letting the first video decide, which is what happens until
 * someone chooses.
 */
export function CompositionPanel({
  project,
  caps,
  onChange,
}: {
  project: Project;
  caps: Capabilities;
  onChange: (project: Project) => void;
}) {
  const current = matchingComposition(project);
  const footage = footageSize(project);
  const differsFromFootage =
    footage !== null && (footage.width !== project.width || footage.height !== project.height);

  return (
    <div className="mt-2 flex flex-col gap-2">
      <div role="group" aria-label="Frame size" className="grid grid-cols-6 gap-1">
        {COMPOSITION_PRESETS.map((preset) => (
          <PresetButton
            key={preset.id}
            preset={preset}
            active={current?.id === preset.id}
            onClick={() => onChange(setComposition(project, preset))}
          />
        ))}
      </div>

      <div className="flex items-center gap-1.5">
        <SideField
          label="Width"
          value={project.width}
          onCommit={(width) => onChange(setComposition(project, { width, height: project.height }))}
        />
        <span className="font-mono text-[11px] text-muted">×</span>
        <SideField
          label="Height"
          value={project.height}
          onCommit={(height) => onChange(setComposition(project, { width: project.width, height }))}
        />
        <button
          type="button"
          title="Swap width and height"
          aria-label="Swap width and height"
          onClick={() => onChange(setComposition(project, { width: project.height, height: project.width }))}
          className="flex size-7 shrink-0 items-center justify-center rounded-[7px] border border-rule text-pine transition-colors hover:border-pine hover:bg-cream-2"
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M4 15V9a2 2 0 0 1 2-2h11" />
            <path d="m14 4 3 3-3 3" />
            <path d="M20 9v6a2 2 0 0 1-2 2H7" />
            <path d="m10 20-3-3 3-3" />
          </svg>
        </button>
      </div>

      <p className="flex items-baseline justify-between gap-2 font-mono text-[10px] uppercase tracking-[0.12em] text-muted">
        <span>
          {aspectLabel(project.width, project.height)} · {project.fps} fps
          {caps.label ? ` · ${caps.label}` : ""}
          {caps.label && !caps.audio ? " · no audio" : ""}
        </span>
        {project.sizedByHand && differsFromFootage ? (
          <button
            type="button"
            className="link shrink-0 normal-case tracking-normal text-pine"
            title={`Back to the footage's own ${footage.width}×${footage.height}`}
            onClick={() => onChange(unsizeComposition(project))}
          >
            Match footage
          </button>
        ) : null}
      </p>
    </div>
  );
}

/** The label people say: 16:9, 9:16, 1:1, 4:5. Anything else is shown as a ratio to one. */
export function aspectLabel(width: number, height: number): string {
  const known: [number, number][] = [
    [16, 9],
    [9, 16],
    [1, 1],
    [4, 5],
    [4, 3],
    [3, 4],
    [21, 9],
    [2, 3],
    [3, 2],
  ];
  for (const [w, h] of known) {
    if (Math.abs(width / height - w / h) < 0.005) return `${w}:${h}`;
  }
  return `${(width / height).toFixed(2)}:1`;
}

/** The shape drawn as it is, so the row can be read without reading. */
function PresetButton({
  preset,
  active,
  onClick,
}: {
  preset: CompositionPreset;
  active: boolean;
  onClick: () => void;
}) {
  const aspect = preset.width / preset.height;
  const w = aspect >= 1 ? 18 : 18 * aspect;
  const h = aspect >= 1 ? 18 / aspect : 18;
  return (
    <button
      type="button"
      aria-pressed={active}
      aria-label={`${preset.label}, ${preset.width}×${preset.height}`}
      title={`${preset.label} · ${preset.hint} · ${preset.width}×${preset.height}`}
      onClick={onClick}
      className={`flex flex-col items-center gap-1 rounded-[6px] border px-0.5 py-1.5 transition-colors ${
        active ? "border-pine-deep bg-pine text-cream" : "border-rule bg-cream-2/40 text-pine hover:border-pine hover:bg-cream-2"
      }`}
    >
      <span className="flex h-[18px] w-[18px] items-center justify-center" aria-hidden="true">
        <span
          className={`block rounded-[2px] border ${active ? "border-cream/80 bg-cream/20" : "border-current bg-current/10"}`}
          style={{ width: w, height: h }}
        />
      </span>
      <span className="text-[8.5px] font-medium leading-none">{preset.label}</span>
    </button>
  );
}

/**
 * One side of the frame, in pixels. Committed on blur or Enter rather than on
 * every keystroke, because "1" on the way to "1080" is not a frame anyone
 * wants to see, even for a moment.
 */
function SideField({
  label,
  value,
  onCommit,
}: {
  label: string;
  value: number;
  onCommit: (value: number) => void;
}) {
  const id = useId();
  const [text, setText] = useState(String(value));
  const [editing, setEditing] = useState(false);
  useEffect(() => {
    if (!editing) setText(String(value));
  }, [value, editing]);
  const commit = () => {
    setEditing(false);
    const parsed = Number(text);
    if (Number.isFinite(parsed) && parsed >= MIN_SIDE && parsed <= MAX_SIDE && Math.round(parsed) !== value) {
      onCommit(Math.round(parsed));
    } else {
      setText(String(value));
    }
  };
  return (
    <label
      htmlFor={id}
      className="flex min-w-0 flex-1 items-center gap-1 rounded-[7px] border border-rule bg-[#fbfaf5] px-2"
      title={`${label} in pixels`}
    >
      <span className="font-mono text-[9px] uppercase text-muted">{label[0]}</span>
      <input
        id={id}
        type="number"
        aria-label={`${label} in pixels`}
        min={MIN_SIDE}
        max={MAX_SIDE}
        step={2}
        value={text}
        onFocus={() => setEditing(true)}
        onChange={(event) => setText(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            commit();
            event.currentTarget.blur();
          }
        }}
        className="w-full min-w-0 bg-transparent py-1 text-right font-mono text-[12px] text-ink outline-none"
      />
      <span className="font-mono text-[9px] text-muted">px</span>
    </label>
  );
}

/* ---- animation ----------------------------------------------------------- */

/**
 * How a clip arrives and leaves. The same eight moves for a title, a logo and
 * a picture-in-picture, because they are the same thing to the renderer.
 */
export function AnimationPanel({
  project,
  clip,
  onChange,
}: {
  project: Project;
  clip: Clip;
  onChange: (project: Project) => void;
}) {
  const animation: ClipAnimation = clip.animation ?? { in: "none", out: "none", inDuration: 0.5, outDuration: 0.5 };
  const set = (patch: Partial<ClipAnimation>) => onChange(setAnimation(project, clip.id, patch));
  return (
    <Group label="Animation">
      <MotionRow
        label="In"
        motion={animation.in}
        duration={animation.inDuration}
        onMotion={(motion) => set({ in: motion })}
        onDuration={(inDuration) => set({ inDuration })}
      />
      <MotionRow
        label="Out"
        motion={animation.out}
        duration={animation.outDuration}
        onMotion={(motion) => set({ out: motion })}
        onDuration={(outDuration) => set({ outDuration })}
      />
    </Group>
  );
}

function MotionRow({
  label,
  motion,
  duration,
  onMotion,
  onDuration,
}: {
  label: string;
  motion: Motion;
  duration: number;
  onMotion: (motion: Motion) => void;
  onDuration: (seconds: number) => void;
}) {
  const id = useId();
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between gap-2">
        <label htmlFor={id} className="text-[12px] text-soft">
          {label}
        </label>
        <select
          id={id}
          value={motion}
          onChange={(event) => onMotion(event.target.value as Motion)}
          className="rounded-[7px] border border-rule bg-[#fbfaf5] px-2 py-1 text-[12px] text-ink outline-none focus:border-pine"
        >
          {MOTIONS.map((option) => (
            <option key={option.value} value={option.value} title={option.hint}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
      {motion !== "none" ? (
        <Slider
          label={`${label} over`}
          value={duration}
          min={0.1}
          max={2}
          step={0.05}
          format={(value) => `${value.toFixed(2)}s`}
          onChange={onDuration}
        />
      ) : null}
    </div>
  );
}
